/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * API Client Cache - Request deduplication cache management
 */

import type { ApiResult } from "./api-client-types";

// ============================================================================
// CACHE CONFIGURATION
// ============================================================================

/** Time-to-live for cached requests - deduplicate within this window */
export const CACHE_TTL = 5000; // 5 seconds

/** Longer coalescing for expensive dashboard GETs (provider + realtime both fetch). */
export const DASHBOARD_GET_DEDUP_TTL_MS = 15_000;

export function getGetRequestDedupeTtlMs(path: string): number {
  return path.startsWith("/api/dashboard") ? DASHBOARD_GET_DEDUP_TTL_MS : CACHE_TTL;
}

// ============================================================================
// CACHE STORAGE
// ============================================================================

/** Request deduplication cache - prevents duplicate concurrent requests */
export const requestCache = new Map<
  string,
  {
    promise: Promise<ApiResult<unknown>>;
    timestamp: number;
    ttlMs: number;
  }
>();

// ============================================================================
// CACHE UTILITIES
// ============================================================================

/**
 * Generate a unique key for a request based on path, method, and body
 */
export function getRequestKey(
  path: string,
  method: string,
  body?: unknown,
): string {
  const bodyStr = body ? JSON.stringify(body) : "";
  return `${method}:${path}:${bodyStr}`;
}

/**
 * Clean up expired entries from the request cache
 */
export function cleanupCache(): void {
  const now = Date.now();
  for (const [key, value] of requestCache.entries()) {
    if (now - value.timestamp > value.ttlMs) {
      requestCache.delete(key);
    }
  }
}

/**
 * Check if a cached request exists and is still valid
 */
export function getCachedRequest<T>(
  requestKey: string,
): Promise<ApiResult<T>> | null {
  const cached = requestCache.get(requestKey);
  const now = Date.now();

  if (cached && now - cached.timestamp < cached.ttlMs) {
    return cached.promise as Promise<ApiResult<T>>;
  }

  return null;
}

/**
 * Store a request promise in the cache
 */
export function setCachedRequest<T>(
  requestKey: string,
  promise: Promise<ApiResult<T>>,
  ttlMs: number = CACHE_TTL,
): void {
  requestCache.set(requestKey, {
    promise: promise as Promise<ApiResult<unknown>>,
    timestamp: Date.now(),
    ttlMs,
  });

  // Clean up after request completes
  promise.finally(() => {
    setTimeout(() => {
      requestCache.delete(requestKey);
    }, ttlMs);
  });
}
